type JsonSchema = {
  type: string;
  properties?: Record<string, unknown>;
  required?: string[];
};

type WebMcpTool = {
  name: string;
  description: string;
  inputSchema: JsonSchema;
  execute: (args: Record<string, unknown>) => Promise<unknown> | unknown;
};

type ModelContext = {
  registerTool?: (tool: WebMcpTool) => Promise<void> | void;
  unregisterTool?: (name: string) => Promise<void> | void;
  provideContext?: (input: { tools: WebMcpTool[] }) => Promise<void> | void;
};

function getModelContext(): ModelContext | null {
  const docCtx = (document as Document & { modelContext?: ModelContext }).modelContext;
  const navCtx = (navigator as Navigator & { modelContext?: ModelContext }).modelContext;
  return docCtx || navCtx || null;
}

const SITE_TOOLS: WebMcpTool[] = [
  {
    name: 'open_home',
    description: 'Open the Voxonics homepage.',
    inputSchema: { type: 'object', properties: {} },
    execute: async () => {
      window.location.assign('/');
      return { ok: true, path: '/' };
    },
  },
  {
    name: 'open_pricing',
    description: 'Open the Voxonics pricing page.',
    inputSchema: { type: 'object', properties: {} },
    execute: async () => {
      window.location.assign('/pricing');
      return { ok: true, path: '/pricing' };
    },
  },
  {
    name: 'open_platform',
    description: 'Open the Voxonics platform overview page.',
    inputSchema: { type: 'object', properties: {} },
    execute: async () => {
      window.location.assign('/platform');
      return { ok: true, path: '/platform' };
    },
  },
  {
    name: 'open_integrations',
    description: 'Open the Voxonics integrations page.',
    inputSchema: { type: 'object', properties: {} },
    execute: async () => {
      window.location.assign('/integrations');
      return { ok: true, path: '/integrations' };
    },
  },
  {
    name: 'open_contact',
    description: 'Open the Voxonics contact form page.',
    inputSchema: {
      type: 'object',
      properties: {
        topic: {
          type: 'string',
          description: 'Optional contact topic query, e.g. api, voice_ai, consultation',
        },
      },
    },
    execute: async (args) => {
      const topic = typeof args.topic === 'string' && args.topic.trim() ? args.topic.trim() : '';
      const path = topic ? `/contact?topic=${encodeURIComponent(topic)}` : '/contact';
      window.location.assign(path);
      return { ok: true, path };
    },
  },
  {
    name: 'open_auth',
    description: 'Open the Voxonics sign-up / sign-in page to start a trial.',
    inputSchema: { type: 'object', properties: {} },
    execute: async () => {
      window.location.assign('/auth');
      return { ok: true, path: '/auth' };
    },
  },
  {
    name: 'get_site_overview',
    description:
      'Return a short overview of Voxonics products and key public URLs for agents.',
    inputSchema: { type: 'object', properties: {} },
    execute: async () => ({
      name: 'Voxonics',
      summary:
        'Telco-licensed AI communications platform: Cloud PBX, Virtual AI Voice Agents, Auto Dialer, Smart Routing, global VoIP.',
      urls: {
        home: 'https://www.voxonics.com/',
        platform: 'https://www.voxonics.com/platform',
        pricing: 'https://www.voxonics.com/pricing',
        integrations: 'https://www.voxonics.com/integrations',
        contact: 'https://www.voxonics.com/contact',
        auth: 'https://www.voxonics.com/auth',
        llms: 'https://www.voxonics.com/llms.txt',
        authMd: 'https://www.voxonics.com/auth.md',
        apiCatalog: 'https://www.voxonics.com/.well-known/api-catalog',
      },
    }),
  },
];

/**
 * Register browser WebMCP tools for AI agents (isitagentready / Chrome WebMCP).
 * Supports both document.modelContext (current) and navigator.modelContext (legacy).
 */
export async function registerWebMcpTools(): Promise<() => void> {
  const ctx = getModelContext();
  if (!ctx) return () => undefined;

  const registered: string[] = [];

  try {
    if (typeof ctx.provideContext === 'function') {
      await ctx.provideContext({ tools: SITE_TOOLS });
      registered.push(...SITE_TOOLS.map((t) => t.name));
    } else if (typeof ctx.registerTool === 'function') {
      for (const tool of SITE_TOOLS) {
        await ctx.registerTool(tool);
        registered.push(tool.name);
      }
    }
  } catch (err) {
    console.warn('[webmcp] tool registration failed:', err);
  }

  return () => {
    if (typeof ctx.unregisterTool !== 'function') return;
    for (const name of registered) {
      try {
        void ctx.unregisterTool?.(name);
      } catch {
        /* ignore cleanup errors */
      }
    }
  };
}
